import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AuditDisplayService } from '../../services/audit-display.service';
import { DatetimeService } from '../../services/datetime.service';
import { AuditLogResponse } from '../../../pages/audit/models/audit.model';

/**
 * Componente presentacional reutilizable para la "línea de tiempo de auditoría"
 * que aparece en el tab Timeline de los detail-modals (contactos, deals, leads,
 * empresas, tareas, productos, formularios, etc.).
 *
 * Es puramente presentacional: el modal contenedor sigue cargando los datos y
 * pasa `logs`/`loading`/`error`/paginación como inputs; este componente emite
 * `pageChange` cuando el usuario pide otra página. La presentación (traducción,
 * diffs, badges, iconos) sale de `AuditDisplayService` — sin config por entidad.
 */
@Component({
  selector: 'app-audit-timeline',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './audit-timeline.component.html'
})
export class AuditTimelineComponent {
  @Input() logs: AuditLogResponse[] = [];
  @Input() loading = false;
  @Input() error = '';
  @Input() currentPage = 0;
  @Input() totalPages = 0;
  @Input() totalElements = 0;
  @Input() emptyMessage = 'No hay eventos de auditoría registrados.';

  /**
   * Inc 25: nombres de entidad (en minúsculas, tal como llegan en `entityName`)
   * cuyos eventos deben mostrar un enlace "Ver detalle" que navega a la entidad
   * relacionada. Ej.: ['task', 'ticket'] en el timeline de un lead para saltar a
   * la tarea/ticket creados desde él. Vacío por defecto → sin enlaces.
   */
  @Input() linkableEntities: string[] = [];

  @Output() pageChange = new EventEmitter<number>();

  /** Inc 25: se emite al pulsar "Ver detalle" de un evento enlazable. */
  @Output() entityLinkClick = new EventEmitter<{ entityName: string; idEntity: string }>();

  /** Log actualmente expandido (diff campo a campo). */
  expandedLogId: number | null = null;

  constructor(
    public auditDisplay: AuditDisplayService,
    public datetime: DatetimeService,
  ) {}

  toggleLogDetail(id: number): void {
    this.expandedLogId = this.expandedLogId === id ? null : id;
  }

  /** Inc 25: ¿este evento apunta a una entidad enlazable con id resoluble? */
  isLinkable(log: AuditLogResponse): boolean {
    const name = (log.entityName ?? '').toLowerCase();
    return !!log.idEntity && this.linkableEntities.includes(name);
  }

  onEntityLinkClick(log: AuditLogResponse, event: Event): void {
    event.stopPropagation();
    if (!this.isLinkable(log)) return;
    this.entityLinkClick.emit({
      entityName: (log.entityName ?? '').toLowerCase(),
      idEntity: log.idEntity,
    });
  }
}
